import React, { useMemo, useState, useEffect } from "react";
import type { Database } from "@/types/database.types";
import { cn } from "@/lib/utils";
import {
  format,
  isSameDay,
  isToday,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
} from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronLeft, ChevronRight } from "lucide-react";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];
type ProjectData = Database["public"]["Functions"]["get_user_projects"]["Returns"][number];

interface CalendarViewModelProps {
  tasks: TaskWithDetailsRow[];
  projects?: ProjectData[];
  onView: (task: TaskWithDetailsRow) => void;
  onTaskDateChange?: (task: TaskWithDetailsRow, newDate: Date) => void;
  isTaskDialogOpen?: boolean;
}

const MAX_VISIBLE_TASKS = 3;

type ViewMode = "week" | "month";
type SortMode = "none" | "priority" | "project";

export function CalendarViewModel({
  tasks,
  projects = [],
  onView,
  onTaskDateChange,
  isTaskDialogOpen = false,
}: CalendarViewModelProps) {
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [sortMode, setSortMode] = useState<SortMode>("priority");
  const [moreTasksDate, setMoreTasksDate] = useState<Date | null>(null);
  const [moreTasksDialogOpen, setMoreTasksDialogOpen] = useState(false);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [clickedDate, setClickedDate] = useState<Date | null>(null);
  const [draggedTask, setDraggedTask] = useState<TaskWithDetailsRow | null>(null);
  const [draggedFromDate, setDraggedFromDate] = useState<Date | null>(null);
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);
  const [assignedUsersMap, setAssignedUsersMap] = useState<
    Record<string, { username: string | null; full_name: string | null; avatar_url: string | null }>
  >({});

  const tasksWithDates = tasks.filter((t) => t.due_date);

  // Create project map for quick lookup
  const projectsMap = useMemo(() => {
    const map: Record<string, string> = {};
    projects.forEach((p) => {
      if (p.project_id) {
        map[p.project_id] = p.project_title || "Untitled Project";
      }
    });
    return map;
  }, [projects]);

  // Extract assigned users from tasks_with_details view data
  useEffect(() => {
    const assignedMap: Record<
      string,
      { username: string | null; full_name: string | null; avatar_url: string | null }
    > = {};

    tasks.forEach((task: TaskWithDetailsRow) => {
      // Use assigned user info from view if available
      if (task.assigned_to && task.assigned_username) {
        assignedMap[task.assigned_to] = {
          username: task.assigned_username,
          full_name: task.assigned_full_name,
          avatar_url: task.assigned_avatar_url,
        };
      }
    });

    setAssignedUsersMap(assignedMap);
  }, [tasks]);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Get project name for a task
  const getProjectName = (task: TaskWithDetailsRow): string | null => {
    if (!task.project_id) return null;
    // Check if task has project_name from view
    if (task.project_name) return task.project_name;
    return projectsMap[task.project_id] || null;
  };

  // Priority order: high > medium > low > null
  const getPriorityOrder = (priority: string | null | undefined): number => {
    if (priority === "high") return 0;
    if (priority === "medium") return 1;
    if (priority === "low") return 2;
    return 3; // null or undefined
  };

  // Group tasks by date and apply sorting
  const tasksByDate = useMemo(() => {
    const grouped: Record<string, TaskWithDetailsRow[]> = {};
    tasksWithDates.forEach((task) => {
      if (!task.due_date) return;
      const dateKey = new Date(task.due_date).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(task);
    });

    // Apply sorting to each date group
    const sorted: Record<string, TaskWithDetailsRow[]> = {};
    Object.keys(grouped).forEach((dateKey) => {
      const tasks = grouped[dateKey];

      if (sortMode === "none") {
        sorted[dateKey] = tasks;
      } else if (sortMode === "priority") {
        const sortedTasks = [...tasks].sort((a, b) => {
          const priorityA = getPriorityOrder(a.priority);
          const priorityB = getPriorityOrder(b.priority);
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          // If same priority, sort by title alphabetically
          return (a.title || "").localeCompare(b.title || "");
        });
        sorted[dateKey] = sortedTasks;
      } else if (sortMode === "project") {
        const sortedTasks = [...tasks].sort((a, b) => {
          // Get project name inline to avoid dependency issue
          const projectA = a.project_name || projectsMap[a.project_id || ""] || "";
          const projectB = b.project_name || projectsMap[b.project_id || ""] || "";
          const projectCompare = projectA.localeCompare(projectB);
          if (projectCompare !== 0) {
            return projectCompare;
          }
          // If same project, sort by title alphabetically
          return (a.title || "").localeCompare(b.title || "");
        });
        sorted[dateKey] = sortedTasks;
      } else {
        sorted[dateKey] = tasks;
      }
    });

    return sorted;
  }, [tasksWithDates, sortMode, projectsMap]);

  // Generate consistent color for a project based on project ID
  const getProjectColor = (projectId: string | null): string => {
    if (!projectId) return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200";

    // Create a hash from project ID to get consistent color
    let hash = 0;
    for (let i = 0; i < projectId.length; i++) {
      hash = projectId.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use a palette of distinct colors
    const colors = [
      "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
      "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200",
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
      "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200",
      "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
      "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
      "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
      "bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200",
    ];

    const index = Math.abs(hash) % colors.length;
    return colors[index]!;
  };

  // Get priority indicator color (left border)
  const getPriorityColor = (priority: string | null | undefined): string => {
    if (priority === "high") return "border-l-2 border-l-red-500";
    if (priority === "medium") return "border-l-2 border-l-yellow-500";
    if (priority === "low") return "border-l-2 border-l-blue-500";
    return "border-l-2 border-l-transparent";
  };

  // Get color bar class for task (for mobile dots - use project color)
  const getTaskColorBar = (task: TaskWithDetailsRow): string => {
    if (!task.project_id) return "bg-slate-500";

    // Create a hash from project ID to get consistent color
    let hash = 0;
    for (let i = 0; i < task.project_id.length; i++) {
      hash = task.project_id.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use a palette of distinct colors (just the color part, no text)
    const colors = [
      "bg-blue-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-cyan-500",
      "bg-teal-500",
      "bg-emerald-500",
      "bg-amber-500",
      "bg-orange-500",
      "bg-rose-500",
    ];

    const index = Math.abs(hash) % colors.length;
    return colors[index]!;
  };

  // Generate calendar days based on view mode
  const calendarDays = useMemo(() => {
    if (viewMode === "week") {
      // Week view: show 7 days starting from the start of the week
      const weekStart = startOfWeek(currentWeek);
      const days: Date[] = [];
      for (let i = 0; i < 7; i++) {
        days.push(addDays(weekStart, i));
      }
      return days;
    } else {
      // Month view: show full month with padding days
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const calendarStart = startOfWeek(monthStart);
      const calendarEnd = endOfWeek(monthEnd);

      const days: (Date | null)[] = [];
      let currentDay = calendarStart;

      while (currentDay <= calendarEnd) {
        if (currentDay < monthStart || currentDay > monthEnd) {
          days.push(null); // Outside current month
        } else {
          days.push(new Date(currentDay));
        }
        currentDay = addDays(currentDay, 1);
      }

      return days;
    }
  }, [currentMonth, currentWeek, viewMode]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, task: TaskWithDetailsRow, date: Date) => {
    setDraggedTask(task);
    setDraggedFromDate(date);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", task.id!);

    // Create custom drag image
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.opacity = "0.5";
    dragImage.style.position = "absolute";
    dragImage.style.top = "-1000px";
    dragImage.style.pointerEvents = "none";
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
    }, 0);
  };

  const handleDragEnd = () => {
    setTimeout(() => {
      setDraggedTask(null);
      setDraggedFromDate(null);
      setDragOverDate(null);
    }, 100);
  };

  const handleDragOver = (e: React.DragEvent, date: Date | null) => {
    if (!date) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverDate(date);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverDate(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetDate: Date | null) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDate(null);

    if (!draggedTask || !draggedFromDate || !targetDate || !onTaskDateChange) return;

    // Only update if the date actually changed
    if (!isSameDay(draggedFromDate, targetDate)) {
      const newDate = new Date(targetDate);
      newDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
      onTaskDateChange(draggedTask, newDate);
    }

    setDraggedTask(null);
    setDraggedFromDate(null);
  };

  const changeMonth = (offset: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };

  const changeWeek = (offset: number) => {
    setCurrentWeek(addWeeks(currentWeek, offset));
  };

  const handleViewModeChange = (value: string) => {
    if (value === "week" || value === "month") {
      // When switching to week view, use the current month's date to determine the week
      if (value === "week") {
        setCurrentWeek(currentMonth);
      } else {
        // When switching to month view, use the current week's date to determine the month
        setCurrentMonth(currentWeek);
      }
      setViewMode(value);
    }
  };

  const monthName = format(currentMonth, "MMMM yyyy");
  const weekRange =
    viewMode === "week"
      ? (() => {
          const weekStart = startOfWeek(currentWeek);
          const weekEnd = endOfWeek(currentWeek);
          if (weekStart.getMonth() === weekEnd.getMonth()) {
            return format(weekStart, "MMMM d") + " – " + format(weekEnd, "d, yyyy");
          } else {
            return format(weekStart, "MMM d") + " – " + format(weekEnd, "MMM d, yyyy");
          }
        })()
      : monthName;
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const selectedDateTasks = tasksByDate[selectedDate.toDateString()] || [];
  const moreTasksList = moreTasksDate ? tasksByDate[moreTasksDate.toDateString()] || [] : [];
  const clickedDateTasks = clickedDate ? tasksByDate[clickedDate.toDateString()] || [] : [];

  return (
    <>
      <div className="w-full h-full flex flex-col min-h-0">
        {/* Calendar Header */}
        <div className={cn("shrink-0 border-b bg-background px-4 py-2")}>
          <div className="flex items-center justify-between">
            {/* Left: Month and Year */}
            <div className="flex-1">
              <h2 className={cn("font-semibold tracking-tight")}>{weekRange}</h2>
            </div>

            {/* Center: Week/Month Toggle */}
            <div className="flex-1 flex justify-center">
              {!isMobile && (
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={handleViewModeChange}
                  variant="outline"
                  size="sm"
                >
                  <ToggleGroupItem value="week" aria-label="Week view">
                    Week
                  </ToggleGroupItem>
                  <ToggleGroupItem value="month" aria-label="Month view">
                    Month
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
            </div>

            {/* Right: Sort, Pagination */}
            <div className="flex-1 flex items-center justify-end gap-2">
              {/* Sort Selector - Desktop only */}
              {!isMobile && (
                <Select value={sortMode} onValueChange={(value) => setSortMode(value as SortMode)}>
                  <SelectTrigger className="h-9 w-[140px]">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="priority">Priority</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {/* Pagination */}
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-lg hover:bg-accent"
                  onClick={() => (viewMode === "week" ? changeWeek(-1) : changeMonth(-1))}
                  aria-label={viewMode === "week" ? "Previous week" : "Previous month"}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-lg hover:bg-accent"
                  onClick={() => (viewMode === "week" ? changeWeek(1) : changeMonth(1))}
                  aria-label={viewMode === "week" ? "Next week" : "Next month"}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div
          className={cn("flex-1 flex flex-col min-h-0 overflow-hidden", isMobile ? "p-4" : "p-6")}
        >
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-2 mb-3 shrink-0">
            {weekDays.map((day) => (
              <div
                key={day}
                className={cn(
                  "text-center font-medium text-muted-foreground py-2",
                  isMobile ? "text-xs" : "text-sm uppercase tracking-wider",
                )}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days Grid */}
          <div className="grid grid-cols-7 gap-2 flex-1 min-h-0 auto-rows-fr">
            {calendarDays.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="min-h-0" />;
              }

              const dateKey = date.toDateString();
              const dayTasks = tasksByDate[dateKey] || [];
              // In week view, show all tasks; in month view, limit to MAX_VISIBLE_TASKS
              const visibleTasks =
                viewMode === "week" ? dayTasks : dayTasks.slice(0, MAX_VISIBLE_TASKS);
              const hiddenCount = viewMode === "week" ? 0 : dayTasks.length - MAX_VISIBLE_TASKS;
              // In week view, all days are current; in month view, check if it's in current month
              const isCurrentMonth =
                viewMode === "week" ? true : date.getMonth() === currentMonth.getMonth();
              const isTodayDate = isToday(date);
              const isSelected = isSameDay(date, selectedDate);
              const isDragOver = dragOverDate && isSameDay(date, dragOverDate);

              return (
                <div
                  key={dateKey}
                  onDragOver={(e) => handleDragOver(e, date)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, date)}
                  onClick={(e) => {
                    // Don't open dialog if clicking on a task or "more" button
                    const target = e.target as HTMLElement;
                    if (
                      target.closest("[data-task-item]") ||
                      target.closest("[data-more-button]")
                    ) {
                      return;
                    }

                    if (isMobile) {
                      setSelectedDate(date);
                    } else {
                      // Desktop: Open dialog with all tasks for this date
                      setClickedDate(date);
                      setDateDialogOpen(true);
                    }
                  }}
                  className={cn(
                    "rounded-xl p-2 transition-all duration-200 flex flex-col min-h-0 h-full",
                    // Base styles
                    isCurrentMonth
                      ? "bg-card border border-border shadow-sm hover:shadow-md hover:border-border/80"
                      : "bg-muted/20 border border-transparent opacity-40",
                    // Today styling
                    isTodayDate &&
                      isCurrentMonth &&
                      "ring-2 ring-primary/50 bg-primary/5 border-primary/30 shadow-md",
                    // Selected styling (mobile)
                    isSelected &&
                      isMobile &&
                      "ring-2 ring-primary bg-primary/10 border-primary/50 shadow-md",
                    // Drag over styling
                    isDragOver &&
                      isCurrentMonth &&
                      "ring-2 ring-primary bg-primary/10 border-primary shadow-lg scale-[1.02]",
                    // Hover styling (desktop)
                    !isMobile &&
                      isCurrentMonth &&
                      "hover:bg-accent/30 hover:shadow-md cursor-pointer",
                    // Disabled styling for outside month (only in month view)
                    !isCurrentMonth && viewMode === "month" && "cursor-default",
                  )}
                >
                  {/* Day Number */}
                  <div className="flex items-center justify-between mb-1.5">
                    <div
                      className={cn(
                        "text-sm font-semibold transition-colors",
                        isTodayDate && isCurrentMonth && "text-primary",
                        !isCurrentMonth && "text-muted-foreground/60",
                        isCurrentMonth && !isTodayDate && "text-foreground",
                      )}
                    >
                      {date.getDate()}
                    </div>
                    {isTodayDate && isCurrentMonth && (
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </div>

                  {/* Tasks */}
                  {isMobile ? (
                    // Mobile: Show indicator dots
                    dayTasks.length > 0 && (
                      <div className="flex justify-center items-center gap-1 mt-1.5">
                        {dayTasks.length <= 3 ? (
                          dayTasks.map((task) => (
                            <div
                              key={task.id}
                              className={cn("w-1.5 h-1.5 rounded-full", getTaskColorBar(task))}
                            />
                          ))
                        ) : (
                          <>
                            {dayTasks.slice(0, 2).map((task) => (
                              <div
                                key={task.id}
                                className={cn("w-1.5 h-1.5 rounded-full", getTaskColorBar(task))}
                              />
                            ))}
                            <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60" />
                          </>
                        )}
                      </div>
                    )
                  ) : (
                    // Desktop: Show task items
                    <div
                      className={cn(
                        "space-y-1 flex-1",
                        viewMode === "week" ? "overflow-y-auto" : "overflow-hidden",
                      )}
                    >
                      {visibleTasks.map((task) => {
                        const isCompleted = task.status === "done";
                        const projectName = getProjectName(task);
                        return (
                          <div
                            key={task.id}
                            data-task-item
                            draggable
                            onDragStart={(e) => handleDragStart(e, task, date)}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(task);
                            }}
                            className={cn(
                              "text-[11px] px-1.5 py-0.5 rounded text-left truncate w-full cursor-move transition-all flex items-center gap-1 shadow-sm",
                              getProjectColor(task.project_id),
                              getPriorityColor(task.priority),
                              draggedTask?.id === task.id && "opacity-50 scale-95",
                              "hover:shadow-md hover:scale-[1.01] active:scale-95",
                              isCompleted && "opacity-70",
                            )}
                            style={
                              {
                                userSelect: "none",
                                WebkitUserSelect: "none",
                                touchAction: "none",
                                WebkitUserDrag: "element",
                              } as React.CSSProperties & { WebkitUserDrag?: string }
                            }
                            title={`${task.title}${projectName ? ` • ${projectName}` : ""}${task.priority ? ` • ${task.priority} priority` : ""}${isCompleted ? " • Completed" : ""}`}
                          >
                            <span
                              className={cn(
                                "truncate font-medium flex-1",
                                isCompleted && "line-through",
                              )}
                            >
                              {task.title}
                            </span>
                            {task.assigned_to &&
                              (assignedUsersMap[task.assigned_to] || task.assigned_username) && (
                                <Avatar className="h-3.5 w-3.5 shrink-0">
                                  <AvatarImage
                                    src={
                                      assignedUsersMap[task.assigned_to]?.avatar_url ||
                                      task.assigned_avatar_url ||
                                      undefined
                                    }
                                  />
                                  <AvatarFallback className="text-[7px]">
                                    {getInitials(
                                      assignedUsersMap[task.assigned_to]?.full_name ||
                                        task.assigned_full_name ||
                                        assignedUsersMap[task.assigned_to]?.username ||
                                        task.assigned_username,
                                    )}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                          </div>
                        );
                      })}
                      {/* Only show "+x more" button in month view */}
                      {viewMode === "month" && hiddenCount > 0 && (
                        <button
                          data-more-button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMoreTasksDate(date);
                            setMoreTasksDialogOpen(true);
                          }}
                          className="text-[10px] text-muted-foreground px-1.5 py-0.5 hover:text-foreground hover:bg-accent/50 rounded transition-colors text-left cursor-pointer w-full font-medium"
                        >
                          +{hiddenCount} more
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: Tasks List - Scrollable below calendar, only for selected date */}
        {isMobile && (
          <div className="flex-1 overflow-y-auto min-h-0 border-t">
            {selectedDateTasks.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-sm">No tasks for {format(selectedDate, "MMMM d, yyyy")}</p>
              </div>
            ) : (
              <div className="space-y-2 pb-4">
                {/* Date Header */}
                <div className="flex items-center gap-3 mb-3 px-4 pt-4 sticky top-0 bg-background z-10 pb-2 border-b">
                  <div className="flex flex-col items-center min-w-[48px] shrink-0">
                    <span className="text-xs font-medium text-muted-foreground uppercase">
                      {format(selectedDate, "EEE")}
                    </span>
                    <span
                      className={cn(
                        "text-lg font-semibold",
                        isToday(selectedDate) && "text-primary",
                        !isToday(selectedDate) && "text-foreground",
                      )}
                    >
                      {format(selectedDate, "d")}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground shrink-0">
                    {selectedDateTasks.length} {selectedDateTasks.length === 1 ? "task" : "tasks"}
                  </span>
                </div>

                {/* Tasks for selected date */}
                <div className="space-y-2 px-4">
                  {selectedDateTasks.map((task) => {
                    const isCompleted = task.status === "done";

                    return (
                      <div
                        key={task.id}
                        onClick={() => {
                          onView(task);
                        }}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                          "hover:bg-accent active:bg-accent/80",
                          isCompleted && "opacity-60",
                        )}
                      >
                        {/* Color Bar */}
                        <div
                          className={cn(
                            "w-1 h-full min-h-[40px] rounded-full shrink-0",
                            getTaskColorBar(task),
                          )}
                        />

                        {/* Task Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3
                              className={cn(
                                "font-medium text-sm flex-1",
                                isCompleted && "line-through text-muted-foreground",
                                !isCompleted && "text-foreground",
                              )}
                            >
                              {task.title}
                            </h3>
                            {task.priority === "high" && !isCompleted && (
                              <span className="text-xs text-red-500 font-medium shrink-0">!</span>
                            )}
                          </div>

                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {task.description}
                            </p>
                          )}

                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <div className="flex gap-1 flex-wrap items-center">
                              {getProjectName(task) && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                  @{getProjectName(task)}
                                </span>
                              )}
                              {task.tags && task.tags.length > 0 && (
                                <>
                                  {task.tags.slice(0, 3).map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </>
                              )}
                            </div>
                            {task.assigned_to && assignedUsersMap[task.assigned_to] && (
                              <div className="flex items-center gap-1">
                                <Avatar className="h-5 w-5">
                                  <AvatarImage
                                    src={
                                      assignedUsersMap[task.assigned_to]?.avatar_url || undefined
                                    }
                                  />
                                  <AvatarFallback className="text-xs">
                                    {getInitials(
                                      assignedUsersMap[task.assigned_to]?.full_name ||
                                        assignedUsersMap[task.assigned_to]?.username,
                                    )}
                                  </AvatarFallback>
                                </Avatar>
                              </div>
                            )}
                            {task.status === "in_progress" && (
                              <span className="text-xs text-blue-600 dark:text-blue-400">
                                In Progress
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Desktop: Dialog for clicked date */}
        {!isMobile && (
          <Dialog
            open={dateDialogOpen}
            onOpenChange={(open) => {
              // Don't close the date dialog if the task dialog is open
              // This prevents the date dialog from closing when task dialog closes
              if (!open && !isTaskDialogOpen) {
                setDateDialogOpen(false);
              }
            }}
          >
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {clickedDate && format(clickedDate, "EEEE, MMMM d, yyyy")}
                </DialogTitle>
                <DialogDescription>
                  {clickedDateTasks.length === 0
                    ? "No tasks for this date"
                    : `${clickedDateTasks.length} ${clickedDateTasks.length === 1 ? "task" : "tasks"}`}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-2">
                {clickedDateTasks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">No tasks scheduled for this date</p>
                  </div>
                ) : (
                  clickedDateTasks.map((task) => {
                    const isCompleted = task.status === "done";

                    return (
                      <div
                        key={task.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(task);
                        }}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                          "hover:bg-accent",
                          isCompleted && "opacity-60",
                        )}
                      >
                        {/* Color Bar */}
                        <div
                          className={cn(
                            "w-1 h-full min-h-[40px] rounded-full shrink-0",
                            getTaskColorBar(task),
                          )}
                        />

                        {/* Task Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3
                              className={cn(
                                "font-medium text-sm flex-1",
                                isCompleted && "line-through text-muted-foreground",
                                !isCompleted && "text-foreground",
                              )}
                            >
                              {task.title}
                            </h3>
                            {task.priority === "high" && !isCompleted && (
                              <span className="text-xs text-red-500 font-medium shrink-0">!</span>
                            )}
                          </div>

                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-1">{task.description}</p>
                          )}

                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <div className="flex gap-1 flex-wrap items-center">
                              {getProjectName(task) && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                  @{getProjectName(task)}
                                </span>
                              )}
                              {task.tags?.map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                            {task.assigned_to && assignedUsersMap[task.assigned_to] && (
                              <div className="flex items-center gap-1">
                                <Avatar className="h-5 w-5">
                                  <AvatarImage
                                    src={
                                      assignedUsersMap[task.assigned_to]?.avatar_url || undefined
                                    }
                                  />
                                  <AvatarFallback className="text-xs">
                                    {getInitials(
                                      assignedUsersMap[task.assigned_to]?.full_name ||
                                        assignedUsersMap[task.assigned_to]?.username,
                                    )}
                                  </AvatarFallback>
                                </Avatar>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Desktop: Dialog for "More" tasks */}
        {!isMobile && (
          <Dialog open={moreTasksDialogOpen} onOpenChange={setMoreTasksDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {moreTasksDate && format(moreTasksDate, "EEEE, MMMM d, yyyy")}
                </DialogTitle>
                <DialogDescription>
                  {moreTasksList.length} {moreTasksList.length === 1 ? "task" : "tasks"}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-2">
                {moreTasksList.map((task) => {
                  const isCompleted = task.status === "done";

                  return (
                    <div
                      key={task.id}
                      onClick={() => {
                        setMoreTasksDialogOpen(false);
                        onView(task);
                      }}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                        "hover:bg-accent",
                        isCompleted && "opacity-60",
                      )}
                    >
                      {/* Color Bar */}
                      <div
                        className={cn(
                          "w-1 h-full min-h-[40px] rounded-full shrink-0",
                          getTaskColorBar(task),
                        )}
                      />

                      {/* Task Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3
                            className={cn(
                              "font-medium text-sm flex-1",
                              isCompleted && "line-through text-muted-foreground",
                              !isCompleted && "text-foreground",
                            )}
                          >
                            {task.title}
                          </h3>
                          {task.priority === "high" && !isCompleted && (
                            <span className="text-xs text-red-500 font-medium shrink-0">!</span>
                          )}
                        </div>

                        {task.description && (
                          <p className="text-xs text-muted-foreground mt-1">{task.description}</p>
                        )}

                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <div className="flex gap-1 flex-wrap items-center">
                            {getProjectName(task) && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                @{getProjectName(task)}
                              </span>
                            )}
                            {task.tags && task.tags.length > 0 && (
                              <>
                                {task.tags.map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                          {task.assigned_to && assignedUsersMap[task.assigned_to] && (
                            <div className="flex items-center gap-1">
                              <Avatar className="h-5 w-5">
                                <AvatarImage
                                  src={assignedUsersMap[task.assigned_to]?.avatar_url || undefined}
                                />
                                <AvatarFallback className="text-xs">
                                  {getInitials(
                                    assignedUsersMap[task.assigned_to]?.full_name ||
                                      assignedUsersMap[task.assigned_to]?.username,
                                  )}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                          )}
                          {task.status === "in_progress" && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              In Progress
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </>
  );
}